import { ChevronRight, ChevronDown, MoreHorizontal, Plus, Edit, Trash2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export default function MenuItem({
  item,
  level = 0,
  toggleExpand,
  updatePermission,
  toggleEnabled,
  setCurrentParentId,
  setShowAddSubMenuModal,
  permissionsList,
  onEdit,
  onDelete,
  activeMenuId,
  setActiveMenuId,
}) {
  const paddingLeft = level * 16;
  const showActions = activeMenuId === item.id;

  const itemRef = useRef(null);
  const [openUpwards, setOpenUpwards] = useState(false);

  useEffect(() => {
    if (showActions && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const dropdownHeight = 120;
      // If there's not enough space below but enough space above, open upwards
      setOpenUpwards(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [showActions]);

  // IMPORTANT: This getGridColsClass must be IDENTICAL to the one in AccessPrivileges.jsx
  function getGridColsClass(length) {
    const maxCols = 12;
    const colCount = Math.min(length, maxCols);
    return `grid-cols-${colCount}`;
  }

  return (
    <>
      {/* Mobile Layout */}
      {/* This section defines the layout for smaller screens. */}
      <div className="block lg:hidden border-b border-gray-200 bg-white p-3" ref={itemRef}>
        {/* Menu Name and Controls Row for Mobile */}
        <div className="flex items-center justify-between mb-3">
          <div
            className="flex items-center flex-1 min-w-0"
            style={{ paddingLeft: `${paddingLeft}px` }}
          >
            {item.children && item.children.length > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent issues if this div is also clickable
                  toggleExpand(item.id);
                }}
                className="mr-2 p-1 rounded-md hover:bg-gray-100 flex-shrink-0"
              >
                {item.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <div className="w-6 mr-2 flex-shrink-0"></div> // Spacer for alignment
            )}
            <span className="font-medium text-sm truncate">{item.name}</span>
          </div>

          {/* Toggle and Actions for Mobile */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Enabled/Disabled Toggle for Mobile */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={item.enabled}
                onChange={() => toggleEnabled(item.id)}
              />
              <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
            </label>

            {/* More actions for Mobile */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveMenuId(showActions ? null : item.id);
                }}
                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                aria-label="More actions"
              >
                <MoreHorizontal size={16} />
              </button>

              {showActions && (
                <div
                  className={`absolute right-0 z-10 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 ${
                    openUpwards ? 'bottom-full mb-1' : 'top-8'
                  }`}
                >
                  <div className="py-1">
                    {item.children && (
                      <button
                        onClick={() => {
                          setCurrentParentId(item.id);
                          setShowAddSubMenuModal(true);
                          setActiveMenuId(null);
                        }}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <Plus size={14} className="mr-2" />
                        Add Submenu
                      </button>
                    )}
                    <button
                      onClick={() => {
                        onEdit(item);
                        setActiveMenuId(null);
                      }}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      <Edit size={14} className="mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        onDelete(item.id);
                        setActiveMenuId(null);
                      }}
                      className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Permissions Grid - Mobile */}
        <div className={`grid gap-2 ${getGridColsClass(permissionsList.length)}`}>
          {permissionsList.map((permission) => (
            <div key={permission.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                checked={item.enabled ? (item.permissions?.[permission.name.toLowerCase()] || false) : false}
                onChange={(e) => {
                  if (item.enabled) {
                    updatePermission(
                      item.id,
                      permission.name.toLowerCase(),
                      e.target.checked,
                      true,
                      true
                    );
                  }
                }}
                className={`h-4 w-4 flex-shrink-0 ${!item.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!item.enabled}
              />
              <span className="text-xs text-gray-700 truncate">{permission.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      {/* This section defines the layout for larger screens. */}
      <div className="hidden lg:flex items-center border-b border-gray-200 bg-white" ref={itemRef}>
        {/* Menu Name Column - Matches AccessPrivileges.jsx header width */}
        <div
          className="flex items-center py-2 w-60 xl:w-72"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          <div className="flex items-center min-w-0 w-full">
            {item.children && item.children.length > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(item.id);
                }}
                className="mr-1 p-1 rounded-md hover:bg-gray-100 flex-shrink-0"
              >
                {item.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <div className="w-6 mr-1 flex-shrink-0"></div> // Spacer for alignment
            )}
            <span className="truncate text-sm">{item.name}</span>
          </div>
        </div>

        {/* Permissions Grid - Occupies flexible space, matching AccessPrivileges.jsx header */}
        <div className="flex-1 min-w-0">
          <div className={`grid h-full gap-2 px-2 ${getGridColsClass(permissionsList.length)}`}>
            {permissionsList.map((permission) => (
              <div key={permission.id} className="flex justify-center items-center">
                <input
                  type="checkbox"
                  checked={item.enabled ? (item.permissions?.[permission.name.toLowerCase()] || false) : false}
                  onChange={(e) => {
                    if (item.enabled) {
                      updatePermission(
                        item.id,
                        permission.name.toLowerCase(),
                        e.target.checked,
                        true,
                        true
                      );
                    }
                  }}
                  className={`form-checkbox h-4 w-4 text-blue-600 rounded ${!item.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!item.enabled}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Enabled/Disabled Toggle Switch - Matches AccessPrivileges.jsx header width */}
        <div className="w-16 xl:w-20 flex items-center justify-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={item.enabled}
              onChange={() => toggleEnabled(item.id)}
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Actions Menu - Matches AccessPrivileges.jsx header width */}
        <div className="w-16 xl:w-24 flex items-center justify-center relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveMenuId(showActions ? null : item.id);
            }}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            aria-label="More actions"
          >
            <MoreHorizontal size={18} />
          </button>

          {showActions && (
            <div
              className={`absolute right-0 z-10 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 ${
                openUpwards ? 'bottom-full mb-1' : 'top-8'
              }`}
            >
              <div className="py-1">
                {item.children && (
                  <button
                    onClick={() => {
                      setCurrentParentId(item.id);
                      setShowAddSubMenuModal(true);
                      setActiveMenuId(null);
                    }}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                  >
                    <Plus size={16} className="mr-2" />
                    Add Submenu
                  </button>
                )}
                <button
                  onClick={() => {
                    onEdit(item);
                    setActiveMenuId(null);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Edit size={16} className="mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete(item.id);
                    setActiveMenuId(null);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recursive Children Rendering */}
      {item.expanded && item.children && item.children.length > 0 && (
        <div>
          {item.children.map((child) => (
            <MenuItem
              key={child.id}
              item={child}
              level={level + 1}
              toggleExpand={toggleExpand}
              updatePermission={updatePermission}
              toggleEnabled={toggleEnabled}
              setCurrentParentId={setCurrentParentId}
              setShowAddSubMenuModal={setShowAddSubMenuModal}
              permissionsList={permissionsList}
              onEdit={onEdit}
              onDelete={onDelete}
              activeMenuId={activeMenuId}
              setActiveMenuId={setActiveMenuId}
            />
          ))}
        </div>
      )}
    </>
  );
}